//libs
import { useEffect, useState } from "react";
import {
  CheckBadgeIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from "@heroicons/react/20/solid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
//components
import Button from "components/Button";
import Tabs from "components/Tabs";
import GeneralInformation from "./components/GeneralInformation/GeneralInformation";
import QuestionsAndAnswers from "./components/QuestionsAndAnswers/QuestionsAndAnswers";
import TenderProposals from "./components/TenderProposal/TenderProposals";
// import TenderStatus from "features/tenders/components/TenderStatus";
// import TenderType from "features/tenders/components/TenderType";
import Breadcrumbs from "components/Breadcrumbs";
import NDAConfirmationModal from "./components/NDAConfirmationModal";
import CheckListModal from "./components/TenderProposal/CheckListModal";
import CongratulationModal from "./components/TenderProposal/CongratulationModal";
import WithdrawTenderModal from "./components/TenderProposal/WithdrawTenderModal";
//hooks
import {
  useSubmitQuotationProposal,
  useSubmitTenderProposal,
} from "hooks/api/useSubmitProposal";
import {
  useWithdrawQuotationProposal,
  useWithdrawTenderProposal,
} from "hooks/api/useWithdrawProposal";
import {
  useQuotationNDAAccepted,
  useTenderNDAAccepted,
} from "hooks/api/useNDAAccepted";
import { useFetchTenderDetail } from "hooks/api/useTenders";
import { useFetchQuotationDetail } from "hooks/api/useQuotations";
import AwardedMessage from "../TenderList/components/AwardedMessage";
import TenderStatus from "../components/TenderStatus";

const tabs = [
  { label: "Overview", name: "overview" },
  { label: "Q&A", name: "q&a" },
  { label: "Tender Proposals", name: "proposals" },
];

const pages = [
  { name: "Opportunities", href: "/opportunities", current: false },
  { name: "Overview", href: "#", current: true },
];

export const tenderProposalSchema = yup.object().shape({
  proposalComment: yup.string().required("Comment is required"),
  tenderFeesReceipt: yup
    .mixed()
    .test("required", "Tender fees receipt is required", function (value) {
      if (this.parent.feesRequired) {
        return !!value;
      }
      return true;
    })
    .test(
      "fileFormat",
      "Invalid file type. Please upload an image, PDF, DOCX, or XLSX file.",
      function (value) {
        if (!value) return true;
        const allowedExtensions =
          /(\.jpg|\.jpeg|\.png|\.docx|\.xlsx|\.pdf|\.doc)$/i;
        return allowedExtensions.exec(value.name);
      }
    )
    .test(
      "fileSize",
      "File size is too large. Maximum size is 5MB.",
      function (value) {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024; // 5MB
      }
    ),
  items: yup.array().of(
    yup.object().shape({
      quotation_item_id: yup.number().required(),
      is_no_quote: yup.boolean(),
      quantity: yup.number().when("is_no_quote", {
        is: false,
        then: (schema) =>
          schema.required("Quantity is required").positive("Must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
      unit_price: yup.number().when("is_no_quote", {
        is: false,
        then: (schema) =>
          schema
            .required("Unit price is required")
            .positive("Must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
    })
  ),
});

export default function TenderDetail() {
  const { prefixedId } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const isTender = location.pathname.includes("tenders");
  const id =
    prefixedId.split("-").length > 1 ? prefixedId.split("-")[1] : prefixedId;

  const currentPath = location.pathname.split("/").pop();
  const currentTab = tabs.find((tab) => tab.name === currentPath) || tabs[0];
  //differentiate between tender and quotation api hooks
  const useFetchDetail = isTender
    ? useFetchTenderDetail
    : useFetchQuotationDetail;
  const useSubmitProposal = isTender
    ? useSubmitTenderProposal
    : useSubmitQuotationProposal;
  const useWithdrawProposal = isTender
    ? useWithdrawTenderProposal
    : useWithdrawQuotationProposal;
  const useNDAAccepted = isTender
    ? useTenderNDAAccepted
    : useQuotationNDAAccepted;

  //hooks
  const { isPending: isTenderFetching, data } = useFetchDetail(Number(id));
  const tender = data?.data;

  const { isPending: isSubmitting, mutate: mutateSubmitTenderProposal } =
    useSubmitProposal(Number(id));

  const { isPending: isWithdrawing, mutate: mutateWithdrawTenderProposal } =
    useWithdrawProposal(Number(id));

  const { mutate: mutateNDAAccepted } = useNDAAccepted(Number(id));

  //states
  const [selectedTab, setSelectedTab] = useState(currentTab);
  const [isCheckListModalOpen, setIsCheckListModalOpen] = useState(false);
  const [isCongratulationModalOpen, setIsCongratulationModalOpen] =
    useState(false);
  const [isNDAModalOpen, setIsNDAModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const [tenderFeesReceipt, setTenderFeesReceipt] = useState();
  const [proposalComment, setProposalComment] = useState("");
  const [checklist, setChecklist] = useState([]);

  const proposalStatus = tender?.tender_proposal_status;

  const methods = useForm({
    resolver: yupResolver(tenderProposalSchema),
    defaultValues: {
      proposalComment: "",
      tenderFeesReceipt: "",
      feesRequired: false,
      items: [],
    },
  });

  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    if (tender) {
      setValue("feesRequired", tender.fees_required);
    }
  }, [tender, setValue]);

  const handleTenderSubmit = async () => {
    if (selectedTab !== "proposals") {
      setSelectedTab("proposals");
    }
    handleSubmit(onSubmit)();
  };

  const onSubmit = (data) => {
    setIsCheckListModalOpen(true);
    setTenderFeesReceipt(data.tenderFeesReceipt);
    setProposalComment(data.proposalComment);
  };

  const handleProceed = () => {
    mutateNDAAccepted();
    setSelectedTab("proposals");
  };

  const handleTenderProposalSubmit = () => {
    try {
      const formData = new FormData();
      formData.append(
        isTender ? "tender_fee_receipt" : "quotation_fee_receipt",
        tenderFeesReceipt
      );
      formData.append("proposal_comment", proposalComment);
      formData.append("checklist_before_submit", checklist);

      // Add items data
      const itemsData = methods
        .getValues("items")
        .filter((item) => !item.is_no_quote) // Filter out "no quote" items
        .map((item) => ({
          quotation_item_id: item.quotation_item_id.toString(),
          quantity: Number(item.quantity || 0),
          unit_price: Number(item.unit_price || 0),
        }));

      itemsData.forEach((item, index) => {
        formData.append(
          `items[${index}][quotation_item_id]`,
          item.quotation_item_id
        );
        formData.append(`items[${index}][quantity]`, item.quantity);
        formData.append(`items[${index}][unit_price]`, item.unit_price);
      });

      mutateSubmitTenderProposal(formData);
      setIsCheckListModalOpen(false);
      setIsCongratulationModalOpen(true);
    } catch (err) {}
  };

  const handleTenderProposalWithdraw = ({ cancelReason, cancelComment }) => {
    try {
      const body = {
        cancel_reason: cancelReason,
        cancel_comment: cancelComment,
      };
      mutateWithdrawTenderProposal({
        [isTender ? "tenderProposalId" : "quotationProposalId"]:
          tender.tender_proposal_id,
        body,
      });
    } catch (err) {}
  };

  const handleTabChange = (tabPath) => {
    navigate(`/${isTender ? "tenders" : "quotations"}/${id}/${tabPath}`);
  };

  useEffect(() => {
    handleTabChange(selectedTab.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab.name]);

  if (isTenderFetching) return <></>;

  return (
    <div className="flex items-center justify-center">
      <div className="px-4 pb-8 sm:px-6 lg:px-8 max-w-6xl flex-1">
        <Breadcrumbs pages={pages} />

        {proposalStatus === "pending" && !tender?.is_nda_accepted && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-x-4">
              <Button
                variant="outline"
                color="slate"
                className={"h-8 font-bold"}
                onClick={() => navigate("/opportunities")}
              >
                <ArrowUturnLeftIcon className="text-gray-600 size-4" /> back
              </Button>
              <TenderStatus status={tender.tender_status} />
            </div>
            <div className="flex items-center justify-between gap-x-4">
              <h3 className="font-semibold text-xl">{tender.tender_title}</h3>
              <Button
                className="font-light"
                onClick={() => setIsNDAModalOpen(true)}
                disabled={tender.tender_status !== "open"}
              >
                <PlusCircleIcon className="size-5 text-current" />
                Request to Participate
              </Button>
            </div>
          </div>
        )}

        {proposalStatus === "cancelled" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-x-4">
              <Button
                variant="outline"
                color="slate"
                className={"h-8 font-bold"}
                onClick={() => navigate("/opportunities")}
              >
                <ArrowUturnLeftIcon className="text-gray-600 size-4" /> back
              </Button>
              <TenderStatus status={tender.tender_status} />
            </div>
            <div className="flex items-center justify-between gap-x-4">
              <h3 className="font-semibold text-xl">{tender.tender_title}</h3>
              <Button color="danger" className="font-light" disabled>
                <TrashIcon className="size-4 text-current" />
                Cancelled
              </Button>
            </div>
          </div>
        )}

        {!!(proposalStatus === "pending" && !!tender?.is_nda_accepted) && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-x-4">
              <Button
                variant="outline"
                color="slate"
                className={"h-8 font-bold"}
                onClick={() => navigate("/opportunities")}
              >
                <ArrowUturnLeftIcon className="text-gray-600 size-4" /> back
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  color="primary"
                  variant="outline"
                  className={" h-9 font-light"}
                  onClick={handleTenderSubmit}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button onClick={handleTenderSubmit} disabled={isSubmitting}>
                  <CheckBadgeIcon className="size-4 text-current" />
                  Verify and Submit
                </Button>
              </div>
            </div>
            <div className="flex items-start justify-between gap-x-4">
              <div>
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <ArrowUturnRightIcon className="text-current size-4" />
                  <span className="text-xs font-semibold">
                    You are responding to
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-xl">
                    {tender.tender_title}
                  </h3>
                  <div className="flex items-center gap-x-2 mt-1">
                    <span className="text-xs font-medium rounded-md border border-gray-300 p-1">
                      Tender - {tender.tender_no}
                    </span>
                    <span className="text-xs font-medium rounded-md border border-gray-300 p-1">
                      Agency for {tender.department_name}
                    </span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg shadow-md ring-1 ring-gray-900/5 p-4">
                <dl className="flex flex-wrap">
                  <div className="flex-auto">
                    <dt className="text-sm font-normal leading-6 mb-2 text-gray-500">
                      Closing Date:
                    </dt>
                    <dd className=" text-3xl font-light leading-8 text-gray-900">
                      {tender.end_datetime
                        ? format(new Date(tender.end_datetime), "dd MMM yyyy")
                        : "N/A"}
                    </dd>
                    <dd className="text-3xl font-light leading-8 text-gray-900">
                      {tender.end_datetime
                        ? format(new Date(tender.end_datetime), "HH:mm a")
                        : "N/A"}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}

        {proposalStatus === "proposed" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-x-4">
              <Button
                variant="outline"
                color="slate"
                className={"h-8 font-bold"}
                onClick={() => navigate("/opportunities")}
              >
                <ArrowUturnLeftIcon className="text-gray-600 size-4" /> back
              </Button>
              <TenderStatus status={tender.tender_status} />
            </div>
            {!!(
              proposalStatus === "award" ||
              proposalStatus === "awarded" ||
              proposalStatus === "proposed"
            ) && <AwardedMessage />}
            <div>
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <ArrowUturnRightIcon className="text-current size-4" />
                <span className="text-xs font-semibold">
                  You submitted the tender
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-xl">{tender.tender_title}</h3>
                <div className="flex items-center gap-x-2 mt-1">
                  <span className="text-xs font-medium rounded-md border border-gray-300 p-1">
                    Tender - {tender.tender_no}
                  </span>
                  <span className="text-xs font-medium rounded-md border border-gray-300 p-1">
                    Agency for {tender.department_name}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <WithdrawTenderModal
          open={isWithdrawModalOpen}
          setOpen={setIsWithdrawModalOpen}
          onWithdraw={handleTenderProposalWithdraw}
        />

        <NDAConfirmationModal
          ndaDocument={tender?.nda_document}
          open={isNDAModalOpen}
          setOpen={setIsNDAModalOpen}
          onProceed={handleProceed}
        />

        <CheckListModal
          tender={tender}
          checklist={checklist}
          setChecklist={setChecklist}
          open={isCheckListModalOpen}
          setOpen={setIsCheckListModalOpen}
          isSubmitting={isSubmitting}
          handleTenderProposalSubmit={handleTenderProposalSubmit}
        />

        <CongratulationModal
          open={isCongratulationModalOpen}
          setOpen={setIsCongratulationModalOpen}
          setSelectedTab={setSelectedTab}
        />

        <div className="mt-4">
          <Tabs
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onChange={handleTabChange}
          />
        </div>

        <div className="border border-slate-200 rounded-lg p-6 w-full">
          <div className="w-full">
            {selectedTab.name === "overview" && (
              <GeneralInformation tender={tender} />
            )}
          </div>
          <div className="w-full">
            {selectedTab.name === "q&a" && (
              <QuestionsAndAnswers tender={tender} />
            )}
          </div>
          <FormProvider {...methods}>
            <div className="w-full">
              {selectedTab.name === "proposals" && (
                <TenderProposals
                  tender={tender}
                  tenderFeesReceipt={tenderFeesReceipt}
                  setTenderFeesReceipt={setTenderFeesReceipt}
                />
              )}
            </div>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}
